import { useCallback } from "react";
import { toast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { downloadEnvironmentSupportBundle, triggerBlobDownload } from "@/utils/support-bundle";
import { useEnvironment, useCreateEnvironmentLogsToken } from "@/queries/environment-queries";
import type { PlainWorkflowExecutionAction } from "@/queries/automation-queries";
import type JSZip from "jszip";

function addActionMetadata(zip: JSZip, action: PlainWorkflowExecutionAction): void {
    const metadata = {
        id: action.id,
        workflowId: action.metadata?.workflowId,
        workflowExecutionId: action.metadata?.workflowExecutionId,
        actionName: action.metadata?.actionName,
        startedAt: action.metadata?.startedAt,
        finishedAt: action.metadata?.finishedAt,
        phase: action.status?.phase,
        environmentId: action.status?.environmentId,
        agentExecutionId: action.status?.agentExecutionId,
        stepStatuses: action.status?.stepStatuses,
        failureMessage: action.status?.failureMessage,
        warningMessage: action.status?.warningMessage,
        failures: action.status?.failures,
        warnings: action.status?.warnings,
        spec: action.spec,
        exportedAt: new Date().toISOString(),
    };

    // Custom replacer to handle BigInt values (e.g., Timestamp.seconds from protobuf)
    const replacer = (_key: string, value: unknown) => (typeof value === "bigint" ? value.toString() : value);

    zip.file("action-metadata.json", JSON.stringify(metadata, replacer, 2));
}

export const useWorkflowActionSupportBundle = (action?: PlainWorkflowExecutionAction) => {
    const environmentId = action?.status?.environmentId;
    const { data: environment } = useEnvironment(environmentId);
    const { data: logsToken } = useCreateEnvironmentLogsToken(environmentId);

    const downloadSupportBundle = useCallback(async () => {
        if (!action?.id) {
            return;
        }

        const supportBundleUrl = environment?.status?.environmentUrls?.supportBundle;

        if (!supportBundleUrl || !logsToken) {
            return;
        }

        try {
            const JSZip = (await import("jszip")).default;
            const zip = new JSZip();

            await downloadEnvironmentSupportBundle(zip, supportBundleUrl, logsToken);
            addActionMetadata(zip, action);

            const zipBlob = await zip.generateAsync({ type: "blob" });
            triggerBlobDownload(zipBlob, `action-support-bundle-${action.id}.zip`);
            toast({ title: "Support bundle download started" });
        } catch (error) {
            toast({
                title: "Failed to download support bundle",
                description: formatError(error),
            });
        }
    }, [environment?.status?.environmentUrls?.supportBundle, logsToken, action]);

    const isSupportBundleAvailable = !!environment?.status?.environmentUrls?.supportBundle && !!logsToken;

    return {
        downloadSupportBundle,
        isSupportBundleAvailable,
        environment,
    };
};
